import React, { useState, useEffect } from "react";
import Layout from "../../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { MASTER_API,PURCHASE_API } from "../../apiConfig";
import { toast } from "react-toastify";

const getFinancialYears = () => {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 5; y <= currentYear + 5; y++) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
};

const toOptions = (arr) => arr.map((v) => ({ value: v, label: v }));

const FINANCIAL_YEARS = toOptions(getFinancialYears());

const MONTHS = toOptions([
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March",
]);


const DIVISIONS = toOptions(["Division A", "Division B", "Division C"]); // adjust
const DEPARTMENTS = toOptions(["Finance", "Production", "Sales", "Procurement", "Quality"]); // adjust

// Same style object as Product.js -> customSelectStyles
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

function BudgetPlanningForm() {
  const [formData, setFormData] = useState({
    financialYear: null,
    month: null,
    category: null,
    division: null,
    department: null,
    budget: "",
  });

  const [entries, setEntries] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);   // ADDED

const handleSelectChange = (field) => (option) => {
    setFormData((prev) => ({ ...prev, [field]: option }));
  };

  // ADDED — fetch category dropdown from configuration table
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/category`);
      const options = (res.data || []).map((c) => ({
        value: c.name || c,
        label: c.name || c,
      }));
      setCategoryOptions(options);
    } catch (err) {
      console.error("Failed to fetch categories", err);
      toast.error("Failed to load categories");
    }
  };

  // ADDED — fetch saved budget entries for the table
  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${PURCHASE_API}/budget-planning`);
      setEntries(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch budget entries", err);
      toast.error("Failed to load budget entries");
    }
  };

  // ADDED — run both fetches once when the page loads
  useEffect(() => {
    fetchCategories();
    fetchEntries();
  }, []);

  const handleSave = async () => {
    if (!formData.financialYear) return toast.warning("Please select Financial Year");
    if (!formData.budget) return toast.warning("Please enter Budget");

    const payload = {
      financialYear: formData.financialYear?.value || "",
      month: formData.month?.value || "",
      category: formData.category?.value || "",
      division: formData.division?.value || "",
      department: formData.department?.value || "",
      budget: formData.budget,
    };

    try {
      await axios.post(`${PURCHASE_API}/budget-planning`, payload);
      toast.success("Budget saved successfully");

      setFormData({
        financialYear: null,
        month: null,
        category: null,
        division: null,
        department: null,
        budget: "",
      });

      fetchEntries(); // refresh table with latest data from DB
    } catch (err) {
      console.error("Failed to save budget entry", err);
      toast.error("Failed to save budget entry");
    }
  };

  return (
    <Layout>
      <div
        style={{
          padding: "30px 40px",
          backgroundColor: "#f8fafc",
          minHeight: "calc(100vh - 60px)",
        }}
      >
        <div style={styles.pageTitleBar}>
          <div>
            <h2 style={styles.pageTitle}>Budget Planning</h2>
            <p style={styles.pageSubtitle}>
              Plan and track budgets across departments and cost centers.
            </p>
          </div>
        </div>

        {/* FILTER CARD */}
        <div style={styles.filterCard}>
          {/* ROW 1: Financial Year only */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ minWidth: 220 }}>
              <label style={styles.compactLabel}>Financial Year</label>
              <Select
                styles={customSelectStyles}
                options={FINANCIAL_YEARS}
                value={formData.financialYear}
                onChange={handleSelectChange("financialYear")}
                placeholder="Select Financial Year"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>

          {/* ROW 2: Month, Category, Division, Department, Budget, Save */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 15,
              alignItems: "flex-end",
            }}
          >
            <div style={styles.fieldBox}>
              <label style={styles.compactLabel}>Month</label>
              <Select
                styles={customSelectStyles}
                options={MONTHS}
                value={formData.month}
                onChange={handleSelectChange("month")}
                placeholder="Select Month"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div style={styles.fieldBox}>
              <label style={styles.compactLabel}>Category</label>
              <Select
                styles={customSelectStyles}
                options={categoryOptions}
                value={formData.category}
                onChange={handleSelectChange("category")}
                placeholder="Select Category"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div style={styles.fieldBox}>
              <label style={styles.compactLabel}>Division</label>
              <Select
                styles={customSelectStyles}
                options={DIVISIONS}
                value={formData.division}
                onChange={handleSelectChange("division")}
                placeholder="Select Division"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div style={styles.fieldBox}>
              <label style={styles.compactLabel}>Department</label>
              <Select
                styles={customSelectStyles}
                options={DEPARTMENTS}
                value={formData.department}
                onChange={handleSelectChange("department")}
                placeholder="Select Department"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div style={styles.fieldBox}>
              <label style={styles.compactLabel}>Budget</label>
              <input
                type="number"
                placeholder="Enter budget"
                style={styles.compactInput}
                className="form-control"
                value={formData.budget}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, budget: e.target.value }))
                }
              />
            </div>

            <div>
              <button style={styles.saveBtn} onClick={handleSave}>
                Save
              </button>
            </div>
          </div>
        </div>

        {/* TABLE OF ENTERED BUDGETS */}
        <div style={{ ...styles.tableContainer, marginTop: 20 }}>
          <table className="table" style={styles.erpTable}>
            <thead>
              <tr>
                <th style={styles.erpTh}>Sl No</th>
                <th style={styles.erpTh}>Financial Year</th>
                <th style={styles.erpTh}>Month</th>
                <th style={styles.erpTh}>Category</th>
                <th style={styles.erpTh}>Division</th>
                <th style={styles.erpTh}>Department</th>
                <th style={styles.erpTh}>Budget</th>
              </tr>
            </thead>
            <tbody>
              {entries.length > 0 ? (
                entries.map((row, idx) => (
                  <tr key={row.id}>
                    <td style={styles.erpTd}>{idx + 1}</td>
                    <td style={styles.erpTd}>{row.financial_year || "-"}</td>
                    <td style={styles.erpTd}>{row.month || "-"}</td>
                    <td style={styles.erpTd}>{row.category || "-"}</td>
                    <td style={styles.erpTd}>{row.division || "-"}</td>
                    <td style={styles.erpTd}>{row.department || "-"}</td>
                    <td style={styles.erpTd}>{row.budget || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center text-muted p-3">
                    No budget entries yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  pageTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  pageTitle: { fontSize: 20, fontWeight: 700, color: "#1e293b" },
  pageSubtitle: { fontSize: 12, color: "#64748b" },
  filterCard: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 15,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  compactLabel: {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "2px",
    color: "#334155",
  },
  compactInput: { fontSize: "13px", height: "32px", padding: "4px 8px" },
  fieldBox: { minWidth: 150, flex: "1 1 150px" },
  saveBtn: {
    height: "34px",
    padding: "0 22px",
    borderRadius: "6px",
    background: "#021843",
    color: "#fff",
    border: "1px solid #021843",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
  },
  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  erpTable: { width: "100%", borderCollapse: "collapse" },
  erpTh: {
    background: "#f8fafc",
    fontSize: 12,
    fontWeight: 600,
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
  },
  erpTd: {
    fontSize: 13,
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
  },
};

export default BudgetPlanningForm;